
import { supabase } from '@/integrations/supabase/client';

/**
 * Base service with common functionality for API calls
 */
export const baseApiService = {
  /**
   * Add retry mechanism for API calls
   * @param apiCallFn Function that makes the API call
   * @param mockDataFn Function that returns mock data
   * @param maxRetries Maximum number of retries
   */
  retryApiCall: async <T>(
    apiCallFn: () => Promise<T>, 
    mockDataFn: () => T, 
    maxRetries: number = 2
  ): Promise<T> => {
    let retries = 0;
    
    while (retries < maxRetries) {
      try {
        return await apiCallFn();
      } catch (error: any) {
        retries++;
        console.log(`API call failed, retry ${retries}/${maxRetries}`);
        
        // If it's the last retry and we're in dev with mock data enabled
        if (retries === maxRetries && 
            (import.meta.env.DEV || import.meta.env.MODE === 'development') && 
            import.meta.env.VITE_USE_MOCK_DATA === 'true') {
          console.warn('Using mock data due to persistent network error');
          return mockDataFn();
        }
        
        // If we still have retries left, wait before trying again
        if (retries < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, 1000 * retries));
        } else {
          throw error; // If we've used all retries, throw the error
        }
      }
    }
    
    throw new Error('Maximum retries reached');
  },
  
  /**
   * Check if mock data is allowed
   */
  useMockData: (): boolean => {
    return import.meta.env.VITE_USE_MOCK_DATA === 'true';
  }
};
